import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { BayclubsAlertService } from '@bayclubs/alert';
import { api, ErrorTranslationService } from '@bayclubs/utils';
import { take } from 'rxjs';
import { camps } from '../../../../../models/models.generated';
import { CartApiService } from '../../../../../services/api/cart-api.service';
import { CampsEnumTranslationService } from '../../../../../services/camps-enum-translation.service';
import { PurchaseEditEventService } from '../../purchase-edit-event.service';

@Component({
    selector: 'app-purchase-edit-session-item',
    templateUrl: './purchase-edit-session-item.component.html',
    styleUrls: [ './purchase-edit-session-item.component.scss' ],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PurchaseEditSessionItemComponent {
    item = input.required<camps.OfficeCartSessionInfo>()
    purchaseSetId = input.required<string>();
    canRemoveOptions = input<boolean>(false);

    isPopoverVisible = signal(false);
    options = computed(() => [
        ...(this.item()?.options ?? []).map(option => ({ ...option, isRemoved: false })),
        ...(this.item()?.removedOptions ?? []).map(option => ({ ...option, isRemoved: true }))
    ].sort((a, b) => a.name.localeCompare(b.name)));
    timeVariant = computed(() => this.translateService.timeVariant(this.item()?.timeVariant.type));

    
    constructor(
        private translateService: CampsEnumTranslationService,
        private cartApi: CartApiService,
        private purchaseEditEvent: PurchaseEditEventService,
        private alert: BayclubsAlertService,
        private errorTranslate: ErrorTranslationService
    ) {
    }

    public onAddBackToPurchase(): void {
        const request = <camps.OfficeCartAddSessionRequest>{
            purchaseSetId: this.purchaseSetId(),
            parentPurchasedCampSessionId: this.item().purchasedCampSessionId, // because purchased is parent - when session is removed we have original
            childId: this.item().childId,
            dayVariantId: this.item().daysVariant.id,
            sessionId: this.item().sessionId,
            timeVarianId: this.item().timeVariant.id,
            selectedOptionsIds: this.item().options.map(option => {
                return option.id;
            }),
            customDays: null,
            hasCustomVariant: null,
            campType: this.item().campType
        };

        this.cartApi.addSession(request).pipe(
            take(1)
        ).subscribe(
            (response: api.OperationResultValue<camps.OfficeCartInfo>) => {
                if (response.isSuccess) {
                    this.purchaseEditEvent.onCartChanged(response.value);
                } else {
                    this.errorTranslate.translateValidationNotifications(response.validationNotification);
                    this.alert.showError(response.validationNotification.errors[0].message);
                }
            },
            error => {
                console.log(error);
            },
            () => {
            }
        );
    }

    public onRemoveSession(): void {
        const request = <camps.OfficeCartRemoveSessionRequest>{
            purchaseSetId: this.purchaseSetId(),
            purchasedCampSessionId: this.item().purchasedCampSessionId
        };

        this.cartApi.removeSession(request).pipe(
            take(1)
        ).subscribe(
            (response: api.OperationResultValue<camps.OfficeCartInfo>) => {
                if (response.isSuccess) {
                    this.purchaseEditEvent.onCartChanged(response.value);
                } else {
                    this.errorTranslate.translateValidationNotifications(response.validationNotification);
                    this.alert.showError(response.validationNotification.errors[0].message);
                }
            },
            error => {
                console.log(error);
            },
            () => {
            }
        );
    }

    public onRemoveOption(optionId: string): void {
        const request = <camps.OfficeCartRemoveOptionRequest>{
            purchaseSetId: this.purchaseSetId(),
            purchasedCampSessionId: this.item().purchasedCampSessionId,
            purchasedOptionId: optionId
        };
        this.cartApi.removeOption(request).pipe(
            take(1)
        ).subscribe(
            (response: api.OperationResultValue<camps.OfficeCartInfo>) => {
                if (response.isSuccess) {
                    this.purchaseEditEvent.onCartChanged(response.value);
                } else {
                    this.errorTranslate.translateValidationNotifications(response.validationNotification);
                    this.alert.showError(response.validationNotification.errors[0].message);
                }
            },
            error => {
                console.log(error);
            },
            () => {
            }
        );
    }

    public onAddOption(optionId: string): void {
        const request = <camps.OfficeCartAddOptionRequest>{
            purchaseSetId: this.purchaseSetId(),
            purchasedCampSessionId: this.item().purchasedCampSessionId,
            purchasedOptionId: optionId
        };
        this.cartApi.addOption(request).pipe(
            take(1)
        ).subscribe(
            (response: api.OperationResultValue<camps.OfficeCartInfo>) => {
                if (response.isSuccess) {
                    this.purchaseEditEvent.onCartChanged(response.value);
                } else {
                    this.errorTranslate.translateValidationNotifications(response.validationNotification);
                    this.alert.showError(response.validationNotification.errors[0].message);
                }
            },
            error => {
                console.log(error);
            },
            () => {
            }
        );
    }
}
